import React from 'react'
import { Navigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { User } from '@/services/authService'
import { ROUTES } from '../routes'

interface RoleBasedRouteProps {
  children: React.ReactNode
  checkAccess: (user: User | null) => boolean
}

/**
 * RoleBasedRoute Guard
 *
 * Renders children if the user has permission to access the route.
 * Redirects to /social-media if permission is denied.
 *
 * Example usage:
 * <RoleBasedRoute checkAccess={canAccessSupportPage}>
 *   <SupportDashboard />
 * </RoleBasedRoute>
 */
export const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({
  children,
  checkAccess,
}) => {
  const { user } = useAuthStore()

  // Check if user has permission to access this route
  const hasPermission = checkAccess(user)

  if (!hasPermission) {
    // User doesn't have permission, redirect to default page
    return <Navigate to={ROUTES.SOCIAL_MEDIA} replace />
  }

  // User has permission, render the protected content
  return <>{children}</>
}
